import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { sortBy as _sortBy } from 'lodash';

import { Currency, FileWithTask, Theme, Box } from '@df/interfaces';
import {
    DEFAULT_TABLE_ROWS,
    TABLE_ROWS_KEY,
    TABLE_ROWS_KEYS,
    TABLE_ROWS_NAMES
} from '@app/teacher/components/analysis/analysis-list-subject-home/summary-data/summary.constants';

import { TeacherService } from '@app/teacher/services/teacher.service';
import { TaskService } from '@app/core/services/task.service';
import { AnalyticService } from '@app/teacher/services/analytic.service';

import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { BsModalService } from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-summary-data',
    templateUrl: './summary-data.component.html',
    styleUrls: ['./summary-data.component.scss']
})
export class SummaryDataComponent implements OnInit, OnDestroy {
    public modalRef: BsModalRef;
    selectedTableRows: string[];
    summaryData: any = [];
    selectedLessonsCount: number = 11;
    lessonCounts: any[] = [
        { title: '11', value: 11 },
        { title: '21', value: 21 },
        { title: 'все', value: 'all' }
    ];
    currentLessonNumber: string;
    currentLessonId: string;
    currency: Currency;
    allAbsent;

    colors = {
        text: '#495660',
        speed: '#495660',
        mistakes: '#ca363a',
        earned: '#4e77af',
        successStudents: '#6795c6'
    };

    public resolveSpeedGraphLabels: Array<string> = ['Занятия', 'Ср. скорость решения вариантов'];
    public errorPercentGraphLabels: Array<string> = ['Занятия', 'Средний % ошибок'];
    public averageCurrenciesGraphLabels: Array<string> = ['Занятия', 'Ср. скорость зарабатывания'];
    public filesGraphLabels: Array<string> = ['Занятия', 'Кол-во успешных учеников'];

    resolveSpeedGraphData: any = null;
    errorPercentGraphData: any = null;
    averageCurrenciesGraphData: any = null;
    filesGraphData: any = null;

    themes: Theme[] = [];
    files: FileWithTask[] = [];
    selectedFiles: FileWithTask[] = [];
    boxes: Array<any>;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private modalService: BsModalService,
        private route: ActivatedRoute,
        private teacherService: TeacherService,
        private analyticsService: AnalyticService,
        private taskService: TaskService
    ) {}

    public ngOnInit(): void {
        this.route.parent.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const lesson_id = params['lesson_id'];
            if (!!lesson_id) {
                this.teacherService
                    .getSummaryTable(lesson_id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(response => {
                        this.summaryData = response;
                        this.boxes = _sortBy(response.box_data, ['level', 'title']);
                        this.currency = response.currency;
                        this.allAbsent = true;
                        this.ifStudentsAbsent(response.box_data);
                        if (!this.allAbsent) {
                            this.loadTableRows();
                        }
                    });
            }
        });

        this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
            this.currentLessonNumber = data && data.lesson ? data.lesson.full_lesson_number : null;
        });

        this.route.parent.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            this.currentLessonId = params.lesson_id;
            this.updateAllGraphs();
        });

        if (!this.allAbsent) {
            this.loadFiles();
            this.loadThemes();
        }
    }

    public ngOnDestroy(): void {
        this.allAbsent = false;
        this.destroy$.next(true);
    }

    loadThemes() {
        this.teacherService
            .getLessonsTheme()
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
                this.themes = data.results || [];
            });
    }

    loadFiles() {
        this.taskService
            .getTaskFiles()
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
                this.files = data.results || [];
            });
    }

    updateAllGraphs() {
        this.updateGraphs();
        this.updateFileGraph();
    }

    updateGraphs() {
        this.resolveSpeedGraphData = null;
        this.errorPercentGraphData = null;
        this.averageCurrenciesGraphData = null;

        this.analyticsService
            .getAverageSpeedGraph(this.currentLessonId, this.selectedLessonsCount)
            .pipe(takeUntil(this.destroy$))
            .subscribe(next => {
                this.buildResolveSpeedGraphData(next);
            });
        this.analyticsService
            .getAverageCurrenciesGraph(this.currentLessonId, this.selectedLessonsCount)
            .subscribe(next => {
                this.buildAverageCurrenciesAmountGraphData(next);
            });
        this.analyticsService.getAverageErrorGraph(this.currentLessonId, this.selectedLessonsCount).subscribe(next => {
            this.buildErrorsPercentGraphData(next);
        });
    }

    updateFileGraph() {
        this.filesGraphData = null;
        if (this.selectedFiles.length) {
            const themesParam = this.selectedFiles.map(f => `themes=${f.subtopic.id}`).join('&');
            this.analyticsService
                .getStudentSolvedGraph(this.currentLessonId, this.selectedLessonsCount, themesParam)
                .subscribe(next => {
                    this.buildFilesGraphData(next);
                });
        }
    }

    public setSelectedFiles(files: Array<FileWithTask>): void {
        this.selectedFiles = files;
        this.updateFileGraph();
    }

    buildFilesGraphData(data: any) {
        const graphData = [];
        data.forEach(d => {
            graphData.push([String(d.full_lesson_number), d.confident_students_count]);
        });
        this.filesGraphData = this.getChartData('Кол-во успешных учеников', graphData, this.colors.successStudents);
    }

    buildResolveSpeedGraphData(data: any) {
        const graphData: any = [];
        data.forEach(d => {
            graphData.push([String(d.full_lesson_number), d.average_student_resolve_speed]);
        });
        this.resolveSpeedGraphData = this.getChartData('Ср. скорость решения вариантов', graphData, this.colors.speed);
    }

    buildAverageCurrenciesAmountGraphData(data: any) {
        const graphData: any = [];
        data.forEach(d => {
            graphData.push([String(d.full_lesson_number), d.average_currencies_amount_speed]);
        });
        this.averageCurrenciesGraphData = this.getChartData(
            'Ср. скорость зарабатывания',
            graphData,
            this.colors.earned
        );
    }

    buildErrorsPercentGraphData(data: any) {
        const graphData: any = [];
        data.forEach(d => {
            graphData.push([String(d.full_lesson_number), d.average_error_percent]);
        });
        this.errorPercentGraphData = this.getChartData('Средний % ошибок', graphData, this.colors.mistakes);
    }

    getChartData(title, data, color) {
        if (!data.length) {
            data.push(['', 0]);
        }

        return {
            chartType: 'LineChart',
            dataTable: data,
            options: {
                title: title,
                height: 210,
                width: 245,
                focusTarget: 'category',
                interpolateNulls: true,
                chartArea: {
                    width: '80%',
                    height: 'auto'
                },
                legend: {
                    position: 'none'
                },
                series: {
                    // Gives each series an axis name that matches the Y-axis below.
                    0: { targetAxisIndex: 0, color: color }
                },
                hAxis: {
                    // gridlines: Они не могут быть нарисованы, потому что названия уроков имеет строковый тип
                    // Но я их оставлю здесь, чтобы знать как их рисовать
                    // @see continuous axis https://developers.google.com/chart/interactive/docs/customizing_axes#Terminology
                    gridlines: {
                        color: '#f1f1f1'
                    },
                    title: 'Занятия',
                    titleTextStyle: {
                        color: this.colors.text,
                        italic: false,
                        bold: true,
                        fontSize: '11px'
                    }
                },
                vAxis: {
                    gridline: {
                        color: '#f1f1f1',
                        count: 5
                    }
                }
            }
        };
    }

    onChartReady(chart: any) {
        if (!this.currentLessonNumber) {
            return;
        }
        const idx = chart.data.dataTable.findIndex(d => {
            return d[0] === this.currentLessonNumber;
        });
        chart.wrapper.visualization.setSelection([{ row: idx - 1, column: null }]);
    }

    loadTableRows() {
        let tableRows: any = window.localStorage.getItem(TABLE_ROWS_KEY);
        if (tableRows) {
            try {
                tableRows = JSON.parse(tableRows);
            } catch (e) {
                tableRows = DEFAULT_TABLE_ROWS;
            }
            this.setTableRows(tableRows);
            return;
        }
        this.setTableRows(DEFAULT_TABLE_ROWS);
    }

    setTableRows(rows: string[] = DEFAULT_TABLE_ROWS) {
        if (!rows) {
            return;
        }
        this.selectedTableRows = rows.filter(r => {
            return TABLE_ROWS_KEYS.indexOf(r) > -1;
        });
        if (!this.selectedTableRows || !Array.isArray(this.selectedTableRows)) {
            this.selectedTableRows = DEFAULT_TABLE_ROWS;
        }
        window.localStorage.setItem(TABLE_ROWS_KEY, JSON.stringify(this.selectedTableRows));
    }

    public getRowName(row: string): string {
        return TABLE_ROWS_NAMES[row] || '';
    }

    openTableSelectRowsDialog(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template, Object.assign({}, {}));
    }

    openTableSubjectsDialog(template: TemplateRef<any>) {
        if (this.themes.length !== 0 && this.files.length !== 0) {
            this.modalRef = this.modalService.show(
                template,
                Object.assign(
                    {},
                    {
                        class: 'custom-modal-dialog summary-modal-dialog'
                    }
                )
            );
        }
    }

    public closeIndividualDataModal(): void {
        this.modalRef.hide();
    }

    public choseIndividualDataTableRows(tableRows) {
        this.setTableRows(tableRows);
    }

    getData(formatingData: any): string {
        try {
            if (!formatingData) {
                return '-';
            }
            return formatingData;
        } catch (e) {
            if (e instanceof TypeError) {
                return '-';
            }
        }
    }

    public getRoundedData(data): string {
        if (data != null) {
            return '' + Math.round(data * 100) / 100;
        }
        return '';
    }

    getMoneyAmountData(moneyString): string {
        if (moneyString !== 'null') {
            const amount = parseFloat(moneyString);
            return '' + amount;
        }
        return '-';
    }

    ifStudentsAbsent(boxData) {
        for (const box of boxData) {
            if (box.total_correct_exercises !== 0 || box.total_incorrect_exercises !== 0) {
                this.allAbsent = false;
                break;
            }
        }
        return this.allAbsent;
    }

    checkBoxForExtraExercise(box: Box) {
        return this.summaryData.box_data.some(item => box.id === item.box.id && item.extra_exercise);
    }
}
